import {
  type JSX,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useFloatingPortalContainer } from "@frontend/contexts";

import { Dialog } from "./Dialog";
import { type DialogDataProps, dialogDataSchema } from "./Dialog.schema";
import type { DialogProps } from "./Dialog.types";
import { DialogClose } from "./DialogClose";
import { DialogContent } from "./DialogContent";
import { DialogHeader } from "./DialogHeader";
import { DialogTitle } from "./DialogTitle";
import { DIALOG_TAG_NAME } from "./constants";

type DialogComponentProps = Partial<DialogDataProps> & {
  ref?: Ref<DialogHandle>;
};

export interface DialogHandle {
  open: (originElement?: HTMLElement) => void;
  close: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [DIALOG_TAG_NAME]: {
      props: DialogComponentProps;
      handle: DialogHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-dialog": HTMLElement & DialogHandle;
  }
}

function RWCDialogComponent({
  ref,
  title,
  size,
  placement,
}: DialogComponentProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const floatingPortalRoot = useFloatingPortalContainer();

  const wasNestedPopupOpenAtPressTime = useRef(false);
  const originRef = useRef<DOMRect | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      open: (originElement) => {
        if (originElement) {
          originRef.current = originElement.getBoundingClientRect();
        }

        setOpen(true);
      },
      close: () => setOpen(false),
    }),
    [],
  );

  // Prevents nested islands' popups
  useEffect(() => {
    if (!floatingPortalRoot) {
      return;
    }

    const portalRoot = floatingPortalRoot;

    function handlePointerDown() {
      wasNestedPopupOpenAtPressTime.current =
        portalRoot.querySelector("[data-open]") != null;
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("mousedown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("mousedown", handlePointerDown, true);
    };
  }, [floatingPortalRoot]);

  const handleOpenChange = useCallback<
    NonNullable<DialogProps["onOpenChange"]>
  >(
    (nextOpen, eventDetails) => {
      const isDismissingBecauseOfNestedPopup =
        !nextOpen &&
        eventDetails.reason === "outside-press" &&
        (floatingPortalRoot?.querySelector("[data-open]") != null ||
          wasNestedPopupOpenAtPressTime.current);

      if (isDismissingBecauseOfNestedPopup) {
        eventDetails.cancel();
        return;
      }

      setOpen(nextOpen);
    },
    [floatingPortalRoot],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} originRef={originRef}>
      <DialogContent size={size} placement={placement}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogClose />
        </DialogHeader>

        <slot />
      </DialogContent>
    </Dialog>
  );
}

componentManager.register(
  rwc(DIALOG_TAG_NAME, {
    component: RWCDialogComponent,
    dataSchema: dialogDataSchema,
    usesSlots: true,
  }),
);
